import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import {
  Dialog,
  Button,
  TextInput,
  Image,
  RadioButton,
  Dropdown,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import HolderCompany from "./HolderCompany";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessagePara,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  ContainerLabel,
} from "./AddEditFinacialInformation.styled";
import "./AddEditFinacialInformation.scss";
import closeIcon from "@icons/ic-close-white.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CKEditorText from "@components/CKEditorText/CKEditorText";

const AddEditFinacialInformation = (props) => {
  const { t, onClose, updateRefeshStatus, site } = props;
  const [isAccountCreating, setIsAccountCreating] = useState(false);
  const [createAccountAPIError, setCreateAccountAPIError] = useState("");
  const [vatRateList, setVatRateList] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);

  const [financialInfoComment, setFinancialInfoComment] = useState(
    site?.financialInfoComment ?? ""
  );

  const [formData, setFormData] = useState({
    budgetType: {
      required: true,
      value: site.budgetType ?? 0,
      error: "",
    },
    isSubcontracting: {
      required: true,
      value: site.isSubcontracting === 1 ? 1 : 0,
      error: "",
    },
    siteBudgetAmount: {
      required: true,
      value: site.siteBudgetAmount ?? "",
      error: "",
    },
    paymentDelegationAmount: {
      required: true,
      value: site.paymentDelegationAmount ?? "",
      error: "",
    },
    invoicingVatRate: {
      required: true,
      value: site.invoicingVatRate?.id ?? "",
      error: "",
    },
    paymentTerms: {
      required: true,
      value: site.paymentTerms?.id ?? "",
      error: "",
    },
    financialInfoComment: {
      value: site?.financialInfoComment ?? "",
      error: "",
      required: true,
    },
    company: {
      required: true,
      value:
        {
          id: site.delegatedCompany?.id ? site.delegatedCompany?.id : "",
          name: site.delegatedCompany?.id
            ? site.delegatedCompany?.name
            : site.delegatedCompany?.nameText,
        } ?? "",
      error: "",
    },
  });

  const onBudgetTypeSelect = (e) => {
    setFormData({
      ...formData,
      budgetType: {
        value: parseInt(e.target.value, 10),
        error: "",
        required: true,
      },
    });
  };

  const onSubcontractingChange = () => {
    setFormData({
      ...formData,
      isSubcontracting: {
        value: formData.isSubcontracting.value === 1 ? 0 : 1,
        error: "",
        required: true,
      },
    });
  };

  const onSiteBudgetAmountChange = (e) => {
    setFormData({
      ...formData,
      siteBudgetAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onPaymentDelegationAmountChange = (e) => {
    setFormData({
      ...formData,
      paymentDelegationAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onInvoicingVatRateChange = (e) => {
    setFormData({
      ...formData,
      invoicingVatRate: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onPaymentTermsChange = (e) => {
    setFormData({
      ...formData,
      paymentTerms: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  useEffect(() => {
    setFormData({
      ...formData,
      financialInfoComment: {
        value: financialInfoComment,
        error: "",
        required: true,
      },
    });
  }, [financialInfoComment]);

  const fetchGeneralVatRate = () => {
    axios
      .get(
        `/general/vatRates?countries=[${site.platformCountry.id}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const vatData = response.data.vatRate.map((vats) => {
          return {
            label: vats.label,
            value: vats.id,
            calculationRate: vats.calculationRate,
          };
        });
        setVatRateList(vatData);
      })
      .catch((error) => {});
  };

  const fetchPaymentTerms = () => {
    axios
      .get(
        `/general/paymentTerms?countries=[${site.platformCountry.id}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.paymentTerm.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
          };
        });
        setPaymentTermsList(termData);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    fetchGeneralVatRate();
    fetchPaymentTerms();
  }, []);

  const validateForm = (data) => {
    let isFormValid = true;

    if (data.budgetType?.value === 1) {
      if (!data.siteBudgetAmount.value && data.siteBudgetAmount.value !== 0) {
        isFormValid = false;
        data.siteBudgetAmount.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (
        data.siteBudgetAmount.value &&
        !/^\d+$/.test(data.siteBudgetAmount.value)
      ) {
        isFormValid = false;
        data.siteBudgetAmount.error = t("INVALID_FORMAT");
      }
    }
    if (data.budgetType?.value === 2) {
      if (
        !data.paymentDelegationAmount.value &&
        data.paymentDelegationAmount.value !== 0
      ) {
        isFormValid = false;
        data.paymentDelegationAmount.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (
        data.paymentDelegationAmount.value &&
        !/^\d+$/.test(data.paymentDelegationAmount.value)
      ) {
        isFormValid = false;
        data.paymentDelegationAmount.error = t("INVALID_FORMAT");
      }
      if (!data.company?.value?.id && !data.company?.value?.name) {
        isFormValid = false;
        data.company.error = t("COMPANY_CREDIT_LIMIT_HOLDER_ERROR");
      }
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    let budgetAmount = null;
    let delegatedCompanyInfo = null;
    if (formData.budgetType.value === 1) {
      budgetAmount = formData.siteBudgetAmount.value;
    }
    if (formData.budgetType.value === 2) {
      delegatedCompanyInfo = {
        id: formData.company.value.id,
        name: formData.company.value.name,
        nameText: !formData.company.value.id
          ? formData.company.value.name
          : null,
      };
    }
    const data = {
      id: site.id,
      budgetType: formData.budgetType.value,
      isSubcontracting: formData.isSubcontracting.value,
      paymentTerms: formData.paymentTerms.value,
      invoicingVatRate: formData.invoicingVatRate.value,
      financialInfoComment: formData.financialInfoComment.value,
      siteBudgetAmount: budgetAmount,
      delegatedCompany: delegatedCompanyInfo,
      paymentDelegationAmount:
        formData.budgetType.value === 2
          ? formData.paymentDelegationAmount.value
          : "",
    };
    return data;
  };

  const onSubmit = () => {
    getFormData();
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsAccountCreating(true);
      setCreateAccountAPIError("");
      axios
        .put(`/sites/${site.id}`, getFormData())
        .then(() => {
          setIsAccountCreating(false);
          updateRefeshStatus();
          onClose();
        })
        .catch((error) => {
          setIsAccountCreating(false);
          setCreateAccountAPIError(error.response?.data?.message ?? "");
        });
    }
  };

  return (
    <Dialog width="50rem">
      <div>
        <HeaderWrapper>
          <Title>
            {"#" + site.id}
            {" - " + site.name + " - "} {t("SITE_FINANCIAL_INFORMATION_TITLE")}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <div>
                      <GridRow column="1fr">
                        <ContainerLabel>{t("SITE_BUDGET_TYPE")}</ContainerLabel>
                      </GridRow>
                      <GridRow
                        column="1fr"
                        className="add-edit-finacial-info__radio-button"
                      >
                        <RadioButton
                          value={formData.budgetType.value}
                          onChange={onBudgetTypeSelect}
                          name="price-type"
                          options={[
                            {
                              label: t("SITE_BUDGET_TYPE_CREDIT_LIMIT"),
                              value: 0,
                            },
                            {
                              label: t("SITE_BUDGET_TYPE_SITE_BUDGET"),
                              value: 1,
                            },
                            {
                              label: t("SITE_BUDGET_TYPE_PAYMENT_DELEGATION"),
                              value: 2,
                            },
                          ]}
                        />
                      </GridRow>
                      <GridRow>
                        <div>
                          <Image
                            src={
                              formData.isSubcontracting.value === 1
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("SITE_SUBCONTRACTING_FORM_LABEL")}
                            title={t("SITE_SUBCONTRACTING_FORM_LABEL")}
                            styles={{ marginLeft: "7px" }}
                            onClick={() => onSubcontractingChange()}
                            className="add-edit-finacial-info__image-icon"
                          />
                          <label className="label add-edit-finacial-info__active-label">
                            {t("SITE_SUBCONTRACTING_FORM_LABEL")}
                          </label>
                        </div>
                      </GridRow>
                    </div>
                    <div>
                      {formData.budgetType.value === 1 && (
                        <GridRow paddingBottom="17px">
                          <TextInput
                            id="siteBudgetAmount"
                            type="text"
                            label={t("SITE_BUDGET_AMOUNT")}
                            isFloat
                            required
                            value={String(formData.siteBudgetAmount.value)}
                            onChange={(e) => onSiteBudgetAmountChange(e)}
                            containerStyles={{ marginTop: "21px" }}
                            errorStyles={{ "font-style": "italic" }}
                            labelStyles={
                              formData.siteBudgetAmount.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            inputStyles={
                              formData.siteBudgetAmount.error !== ""
                                ? inputStylesError
                                : inputStyles
                            }
                            error={formData.siteBudgetAmount.error}
                            placeholder={t("SITE_BUDGET_AMOUNT")}
                          />
                        </GridRow>
                      )}
                      {formData.budgetType.value === 2 && (
                        <>
                          <GridRow gap=".50rem">
                            <HolderCompany
                              formData={formData}
                              setFormData={setFormData}
                              selectedHolderCompany={site.delegatedCompany}
                              objectId={site.delegatedCompany?.id}
                            />
                          </GridRow>
                          <GridRow>
                            <TextInput
                              id="paymentDelegationAmount"
                              type="number"
                              label={t("SITE_DELEGATION_AMOUNT")}
                              isFloat
                              required
                              value={String(
                                formData.paymentDelegationAmount.value
                              )}
                              onChange={(e) =>
                                onPaymentDelegationAmountChange(e)
                              }
                              containerStyles={{ marginTop: "21px" }}
                              errorStyles={{ "font-style": "italic" }}
                              labelStyles={
                                formData.paymentDelegationAmount.error !== ""
                                  ? labelStylesError
                                  : labelStyles
                              }
                              inputStyles={
                                formData.paymentDelegationAmount.error !== ""
                                  ? inputStylesError
                                  : inputStyles
                              }
                              error={formData.paymentDelegationAmount.error}
                              placeholder={t("SITE_DELEGATION_AMOUNT")}
                            />
                          </GridRow>
                        </>
                      )}
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="quote-tax-rate"
                      label={t("QUOTE_TAX_RATE")}
                      name="quote-tax-rate"
                      value={formData.invoicingVatRate.value}
                      onChange={onInvoicingVatRateChange}
                      defaultOption={t("QUOTE_TAX_RATE")}
                      options={vatRateList}
                      labelStyles={
                        formData.invoicingVatRate.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.invoicingVatRate.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      containerStyles={{ marginBottom: "7px" }}
                      error={formData.invoicingVatRate.error}
                    />
                    <Dropdown
                      id="quote-payment-terms"
                      label={t("QUOTE_PAYMENT_TERM")}
                      name="quote-payment-terms"
                      value={formData.paymentTerms.value}
                      onChange={onPaymentTermsChange}
                      defaultOption={t("QUOTE_PAYMENT_TERM")}
                      options={paymentTermsList}
                      labelStyles={
                        formData.paymentTerms.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.paymentTerms.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      containerStyles={{ marginBottom: "7px" }}
                      error={formData.paymentTerms.error}
                    />
                  </GridRow>
                  <GridRow column="1fr">
                    <ContainerLabel>
                      {t("COMPANY_FINANCIAL_COMMENT")}
                    </ContainerLabel>
                  </GridRow>
                  <GridRow column="1fr">
                    <CKEditorText
                      descriptionData={financialInfoComment}
                      setDescriptionData={setFinancialInfoComment}
                    />
                    {formData.financialInfoComment.error && (
                      <RequiredMessagePara>
                        {formData.financialInfoComment.error}
                      </RequiredMessagePara>
                    )}
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createAccountAPIError && <div>{createAccountAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isAccountCreating && <ClipLoader size={15} color="#fff" />}
              {!isAccountCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};
AddEditFinacialInformation.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditFinacialInformation.defaultProps = {
  id: null,
};
export default withTranslation()(AddEditFinacialInformation);
